//for navigation at contacts page
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Plus, ClipboardPaste, Tag, List, MessageSquareOff, Loader2, Filter, Settings, Upload, FileInput, Download, FileOutput, Search, Archive } from "lucide-react"
import { useState, useEffect } from "react"
import { FilterPopover } from "./FilterPopover"
import { CategoryFilterPopover } from "./CategoryFilterPopover"
import { useHash } from "@/hooks/useHash"
import { AddToList } from "./AddToList"

export const locationOptions = [
  { value: "california", label: "California" },
  { value: "new-york", label: "New York" },
  { value: "texas", label: "Texas" },
  { value: "florida", label: "Florida" },
  { value: "kolkata", label: "Kolkata" },
  { value: "other", label: "Other" }
]

interface ContactsHeaderProps {
  setShowImportDialog: (value: boolean) => void
  selectedContacts: any[]
  setSelectedContacts: (value: any[]) => void
  searchTerm: string
  setListsFilter: (value: string) => void
  setStatusFilter: (value: string) => void
  setSourceFilter: (value: string) => void
  setLocationFilter: (value: string) => void
  onSearchChange: (value: string) => void
  loading?: boolean
  selectedCount: number
  exporting: boolean
  onExport: () => void
  onAddNew: () => void
  onAddStatus: () => void
  onAddToList: () => void
  onDisable: () => void
  onToggleFilter: () => void
  showFilterOptions: boolean
  statusOptionsList: Array<{ value: string; label: string }>
  sourceOptionsList: Array<{ value: string; label: string }>
  recipientListsList: Array<{ value: string; label: string }>
  onArchive: () => void
}

/**
 * ContactsHeader component
 *
 * This component renders a header for the contacts page.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Array} props.contacts - The array of contacts data to display.
 * @returns {JSX.Element} The rendered contacts header component.
 * @author uplSouvik
 */

export function ContactsHeader({
  setShowImportDialog,
  selectedContacts,
  setSelectedContacts,
  setStatusFilter,
  setSourceFilter,
  setLocationFilter,
  statusOptionsList,
  sourceOptionsList,
  searchTerm,
  onSearchChange,
  loading = false,
  selectedCount,
  exporting,
  onExport,
  onAddNew,
  onAddStatus,
  onAddToList,
  onDisable,
  onToggleFilter,
  showFilterOptions,
  setListsFilter,
  recipientListsList,
  onArchive
}: ContactsHeaderProps) {

  /**
   * this is to check hash routing path
   */
  const hash = useHash();

  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set())
  const [selectedSources, setSelectedSources] = useState<Set<string>>(new Set())
  const [selectedLocations, setSelectedLocations] = useState<Set<string>>(new Set())
  const [selectedLists, setSelectedLists] = useState<Set<string>>(new Set())



  /**
 * Handles the status filter.
 *
 * This function updates the selected statuses state based on the checked status value.
 *
 * @param {string} value - The status value to add or remove from the selected statuses.
 * @param {boolean} checked - Indicates whether the status should be added or removed.
 * @returns {void}
 */
  const handleStatusFilter = (value: string, checked: boolean) => {
    setSelectedStatuses((prev) => {
      const updated = new Set(prev)
      if (checked) {
        updated.add(value)
      } else {
        updated.delete(value)
      }
      return updated
    })
  }

  const handleSourceFilter = (value: string, checked: boolean) => {
    setSelectedSources((prev) => {
      const updated = new Set(prev)
      if (checked) {
        updated.add(value)
      } else {
        updated.delete(value)
      }
      return updated
    })
  }

  /**
 * Handles the location filter.
 *
 * This function updates the selected locations state based on the checked location value.
 *
 * @param {string} value - The location value to add or remove from the selected locations.
 * @param {boolean} checked - Indicates whether the location should be added or removed.
 * @returns {void}
 */

  const handleLocationFilter = (value: string, checked: boolean) => {
    setSelectedLocations((prev) => {
      const updated = new Set(prev)
      if (checked) {
        updated.add(value)
      } else {
        updated.delete(value)
      }
      return updated
    })
  }

  const handleListsFilter = (value: string, checked: boolean) => {
    setSelectedLists((prev) => {
      const updated = new Set(prev)
      if (checked) {
        updated.add(value)
      } else {
        updated.delete(value)
      }
      return updated
    })
  }

  useEffect(() => {
    setStatusFilter(Array.from(selectedStatuses).join("_"))
    setLocationFilter(Array.from(selectedLocations).join("_"))
    setSourceFilter(Array.from(selectedSources).join("_"))
    setListsFilter(Array.from(selectedLists).join("_"))
  }, [selectedStatuses, selectedLocations, selectedSources, selectedLists])


  return (
    <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
      {/* Left side: Filter and Search */}
      <div className="flex items-center gap-3">
        <CategoryFilterPopover
          title="Filter"
          sections={[
            {
              title: "Status",
              options: statusOptionsList,
              selectedValues: selectedStatuses,
              onSelect: (value) => handleStatusFilter(value, !selectedStatuses.has(value))
            },
            {
              title: "Source",
              options: sourceOptionsList,
              selectedValues: selectedSources,
              onSelect: (value) => handleSourceFilter(value, !selectedSources.has(value))
            },
            {
              title: "Lists",
              options: recipientListsList,
              selectedValues: selectedLists,
              onSelect: (value) => handleListsFilter(value, !selectedLists.has(value))
            }
          ]}
          onClearAll={() => {
            setSelectedStatuses(new Set())
            setSelectedSources(new Set())
            setSelectedLists(new Set())
          }}
          icon={Filter}
        />
        <div className="relative group">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
          <Input
            placeholder="Search contacts..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 min-w-[280px] h-10 bg-muted/30 border-muted-foreground/20 focus:bg-background transition-all"
          />
          {loading && (
            <div className="absolute left-1 right-1 -bottom-1 h-0.5 overflow-hidden bg-indigo-50 dark:bg-slate-800 rounded">
              <div className="h-full bg-indigo-600 dark:bg-[#07beff] animate-pulse w-full" style={{ animationDuration: '0.8s' }} />
            </div>
          )}
        </div>
      </div>

      {/* Right side: Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        <AddToList contactLists={selectedContacts} setSelectedContacts={setSelectedContacts} />

        <Button
          size="sm"
          onClick={onAddToList}
          disabled={selectedCount === 0}
          variant="outline"
          className="h-10 px-4 font-semibold hover:bg-primary/5 border-primary/20"
        >
          <List className="h-4 w-4 mr-2 text-primary" />
          Create List ({selectedCount})
        </Button>

        <Button
          size="sm"
          variant="outline"
          onClick={onDisable}
          disabled={selectedCount === 0 || hash === "#create-list"}
          className="h-10 px-4 font-semibold hover:bg-destructive/5 hover:text-destructive border-muted-foreground/20"
        >
          <MessageSquareOff className="h-4 w-4 mr-2" />
          Disabled ({selectedCount})
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onExport}
          disabled={exporting || hash === "#create-list"}
          className="text-[12px] font-bold"
        >
          {exporting ? (
            <>
              <Loader2 className="h-2 w-2 mr-1 animate-spin" />
              Exporting...
            </>
          ) : (
            <>
              <FileOutput className="h-2 w-2 mr-1" />
              Export
            </>
          )}
        </Button>
        {selectedCount === 0 ? (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowImportDialog(true)}
              className="h-10 px-4 font-semibold border-muted-foreground/20"
            >
              <Upload className="h-4 w-4 mr-2" />
              Bulk Import
            </Button>

            <Button
              size="sm"
              className="h-10 px-4 font-semibold shadow-md active:scale-95 transition-all"
              onClick={onAddNew}
              disabled={hash === "#create-list"}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={onArchive}
            disabled={hash === "#create-list"}
            className="h-10 px-4 font-semibold border-muted-foreground/20"
          >
            <Archive className="h-4 w-4 mr-2" />
            Archive ({selectedCount})
          </Button>
        )}
      </div>

    </div>
  )
}


